/**
 * ===========================================================================
 *  NUSANTARA WARRIOR — PLAYER
 * ===========================================================================
 *
 * A state machine over the moveset table. Publishes `world.player`.
 *
 *   MODES   free · attack · dodge · guard · hurt · musou
 *
 *   CONTROLS (see src/input/input.js for the key map)
 *     move        WASD / left stick          light   J / LMB / square
 *     sprint      Shift                      charge  K / RMB / triangle
 *     dodge       Space (tap)                musou   L / MMB / circle
 *     guard       E, or hold Space           lock-on Q / L1
 *     jump        R                          (air combo follows from a jump)
 *
 * ---------------------------------------------------------------------------
 * WHAT MAKES IT FEEL LIKE DYNASTY WARRIORS
 * ---------------------------------------------------------------------------
 * 1. INPUT BUFFER. Every press is buffered for `CFG.combat.inputBuffer`
 *    (0.24 s). You may mash the next hit during the current one's recovery and
 *    it fires the instant the cancel window opens. Nothing is ever eaten.
 * 2. AUTO-FACING. At the MOMENT of the attack input the hero snaps to the
 *    nearest enemy inside a 120-degree cone. DW never makes you fight the
 *    stick; you point roughly and the blade finds the neck.
 * 3. CANCELS. `move.cancel.at` opens the chain; the blade closing opens the
 *    dodge cancel; the musou cancels out of anything after the windup.
 * 4. ROOT MOTION. Attacks travel. The distance is data (`move.root.d`), the
 *    animation's own root track is switched off so nothing double-counts.
 * 5. BRANCHING. Light continues the chain C1..C6; charge branches out of the
 *    CURRENT chain position into a move with a different property.
 * ===========================================================================
 */
import { ORDER } from '../core/systems.js';
import { CFG } from '../core/config.js';
import { KIND, TEAM, ST, FLAG } from '../core/entities.js';
import { angleDamp, clamp, clamp01, wrapPi, ease, lerp } from '../core/mathx.js';
import { MOVESETS, COMMON, F, PRESENTATION, BRAVERY, braveryCapFor, DEFENCE } from '../combat/moveset.js';
import { WALLS, pushOut } from '../world/walls.js';

const _wp = { x: 0, z: 0, nx: 0, nz: 0 };

/**
 * Wading. Depths in metres, `slow` the speed multiplier at `deep` and beyond,
 * `push` the outward acceleration that keeps deep water from being a hole.
 *
 * The Brantas is 4.6 m deep at the centreline, so `deep` at 1.5 puts the
 * slowdown at roughly waist height on a 1.85 m man and the shove somewhere
 * past his chest. A ford stays fordable; the channel does not.
 */
/**
 * RECALIBRATED against the water that is actually there.
 *
 * The first numbers here (shallow 0.35, deep 1.50, push 5.2) were set against
 * a broken `waterAt()` that reported eight metres of water on dry ground. The
 * Brantas measured honestly is a FORD: its bed sits at profile - 4.6 and its
 * surface at profile - 4.05, so the channel is about 0.55 m deep, plus a
 * quarter-metre of sine. Knee deep on a 1.85 m man.
 *
 * So the push is gone. A knee-deep ford does not shove a grown man toward the
 * bank, and pretending it does would be a worse lie than the one it replaced.
 * What is left is a real slowdown through the one crossing on the map — the
 * first thing on it that costs anything but distance.
 */
const WADE = {
  shallow: 0.12,     // below this, no cost at all
  deep: 0.62,        // at this and beyond, the full slowdown
  slow: 0.46,        // speed multiplier there
  outOfDepth: 1.60,  // over a man's head: pushed toward the bank
  push: 4.0,
  /**
   * The depth gameplay is allowed to believe.
   *
   * The river mesh is a flat ribbon at the channel's profile height; it does
   * not follow the bed. Where a bank falls away the ribbon floats above the
   * ground, and `terrain.waterAt()` — which is now faithful to that mesh —
   * reports the gap as depth. A capture caught it: 3.54 m of "water" with the
   * hero standing dry on sand and the ribbon visibly beside him in the air.
   *
   * Those readings are an artifact of a flat ribbon over varying ground, not
   * pools. The honest crossing is the ford at about 0.55 m, and 223 of 238 wet
   * samples are under a metre. So gameplay caps what it will act on: past this
   * the number is the artifact talking. Filed as an open finding — the ribbon
   * should follow the bed — rather than papered over here.
   */
  cap: 1.20,
};
const _water = { depth: 0, px: 0, pz: 0 };

/** Reused: a perfect dodge must not allocate its options object. */
const _ring = { radius: 1.6, life: 0.30, thick: 0.07, alpha: 0.34,
  color: 0xbfe6ff, power: 1, priority: 1 };

/** Every moveset, built once: the contract test walks all of them. */
const MOVESET_LIST = Object.keys(MOVESETS).map((k) => MOVESETS[k]);

/* --------------------------------------------------------------- tuning ---
 * Two numbers the whole-battle pacing turned on. Measured before they existed:
 * a peon's blow put the hero in a 0.30 s hurt lock, and with a ring of eight
 * men swinging he was in that lock more or less permanently — he could not
 * attack at all while anything was on him. And with no recovery of any kind a
 * 2.5-minute victory still ended on 62/1000 hp, so a full-length stage was
 * arithmetically unsurvivable. */
const HURT_GRACE = 0.22;      // s of i-frames after a hit, so a ring cannot stack blows
const REGEN_DELAY = 5.0;      // s out of contact before the hero starts recovering
const REGEN_RATE = 0.016;     // fraction of maxHp per second once he does
const LOCK_RANGE = 26;

/* -------------------------------------------------------- module scratch -- */
const _ids = new Int32Array(512);
const BUFFER_ORDER = ['musou', 'dodge', 'jump', 'heavy', 'light'];
/** Reused option bags — read synchronously by anim/audio, never retained. */
const _popt = { rate: 1, blend: 0.07, hold: 0, lock: 0 };
const _aopt = { x: 0, y: 0, z: 0, gain: 1 };
/** opts for world.vfx.* — field names follow the vfx contract in src/vfx/vfx.js. */
const _vopt = { kind: 'burst', power: 1, scale: 1, element: 'none', radius: 1, life: 0.4, flash: false };
const _play = (r, b, h, l) => { _popt.rate = r; _popt.blend = b; _popt.hold = h; _popt.lock = l; return _popt; };
const _snd = (x, y, z, g) => { _aopt.x = x; _aopt.y = y; _aopt.z = z; _aopt.gain = g; return _aopt; };

export function createPlayer() {
  const P = CFG.player;

  /** `world.player`. Every field here is read by the HUD, the camera, the
   *  combat resolver or the capture harness — keep the names stable. */
  const st = {
    id: -1,
    hp: P.maxHp, maxHp: P.maxHp,
    musou: 0, maxMusou: P.maxMusou, musouReady: false,
    /* BRAVERY (DEV_PLAN Step 3). The spendable resource that turns the five
     * charge branches from buttons into an allocation. Earned by landing hits
     * and by holding guard through a blow; spent by charges (1) and the Battle
     * Art (3). The cap rises with career rank — see braveryCapFor(). */
    bravery: 0, maxBravery: BRAVERY.base,
    braverySpent: 0, braveryEarned: 0, braveryDenied: 0,
    /* DEV_PLAN Step 4. `guardT` is seconds since THIS guard began, and it is
     * the whole of the parry mechanism: the window is simply its first eight
     * frames. Counters are kept so a trace can report defensive actions and
     * the bravery they paid without inferring either. */
    guardT: 0, parries: 0, perfectDodges: 0, braveryFromDefence: 0,
    /* DEV_PLAN Step 7. Riding multiplies what locomote() asks for and what it
     * is allowed to change per second. Both, not just speed: a horse that
     * accelerated like a man would be a speed buff with a model on it. */
    mountSpeed: 1, mountAccel: 1, mounted: false,
    /** Metres of water underfoot; 0 on dry land. Read by the HUD and probes. */
    wading: 0,
    /** 0..1 strength of the current out of a pool, and its direction. */
    current: 0, currentX: 0, currentZ: 0,
    guardHp: 100, maxGuardHp: 100,

    mode: 'free',                 // free | attack | dodge | guard | hurt | musou | dead
    weapon: 'greatGlaive',
    /** Who the player picked on the front end, and where the stage puts him.
     *  `resetBattle()` reads this, so a mission restart keeps the chosen
     *  warrior instead of silently handing the glaive back. */
    name: 'Arya Bhumi', title: 'Wilwatikta Vanguard',
    loadout: { weapon: 'greatGlaive', name: 'Arya Bhumi', title: 'Wilwatikta Vanguard', spawn: { x: 0, z: 30 } },
    allowDeath: true,
    moveId: null, move: null, phase: null,
    moveT: 0, moveF: 0, canCancel: false,

    combo: 0, comboT: 0,          // chain POSITION (which C we are on)
    chain: 0, chainT: 0, maxChain: 0,   // hit chain (KO counter's sibling)
    kills: 0, hits: 0,

    lockId: -1, locked: false,
    guarding: false, guardBreakT: 0,
    sprinting: false, airborne: false, airH: 0, vy: 0, airCombo: 0,
    invulnT: 0, hurtT: 0, regenT: 0,
    musouT: 0, musouPhase: null, musouCycle: 0, musouPulse: 0,
    buffer: null, bufferT: 0,
    speed: 0,
    dbg: { lastMove: '', lastHits: 0, bestSweep: 0, musouPulseSweeps: 0, musouPulseHits: 0 },
  };

  /* ------------------------------------------------------------ internals */
  let W = null;
  let ms = MOVESETS.greatGlaive;          // active moveset
  let kvx = 0, kvz = 0;                   // knockback velocity while hurt
  let rootPrev = 0;                       // root-motion progress already spent
  let extraFired = 0;                     // bitmask of move.extra entries fired
  let wired = false;
  let slowUntil = 0;
  let aura = null;

  /* --------------------------------------------------------------- helpers */

  /** Nearest live enemy within `range`, optionally inside a cone. */
  function pickTarget(w, range, cone, dirX, dirZ) {
    const e = w.entities, id = st.id;
    const px = e.x[id], pz = e.z[id], team = e.team[id];
    const n = e.around(px, pz, range, _ids, _ids.length);
    let best = -1, bestScore = 1e9;
    const cosCone = Math.cos(cone);
    for (let i = 0; i < n; i++) {
      const t = _ids[i];
      if (t === id || !e.alive[t]) continue;
      if (e.team[t] === team || e.team[t] === TEAM.NEUTRAL) continue;
      if (e.state[t] === ST.DEAD) continue;
      const dx = e.x[t] - px, dz = e.z[t] - pz;
      const d = Math.sqrt(dx * dx + dz * dz);
      if (d > range) continue;
      let score = d;
      if (d > 0.05 && cone < Math.PI) {
        const dot = (dx * dirX + dz * dirZ) / d;
        if (dot < cosCone) continue;
        score = d * (1.55 - dot * 0.55);      // straight ahead beats close-but-behind
      }
      // an officer in the cone is worth turning for
      if (e.kind[t] >= KIND.SERGEANT && e.kind[t] <= KIND.GENERAL) score *= 0.72;
      if (score < bestScore) { bestScore = score; best = t; }
    }
    return best;
  }

  /**
   * Snap the hero at the enemy he clearly meant to hit. Called on the exact
   * frame the attack input is accepted — this is the single biggest thing
   * separating "action game" from "physics demo".
   */
  function autoFace(w, range) {
    const e = w.entities, id = st.id;
    let dx = st.wantX, dz = st.wantZ;
    if (!(dx * dx + dz * dz > 0.04)) { dx = Math.sin(e.yaw[id]); dz = Math.cos(e.yaw[id]); }

    let t = -1;
    if (st.locked && st.lockId >= 0 && e.alive[st.lockId] && e.state[st.lockId] !== ST.DEAD) t = st.lockId;
    if (t < 0) t = pickTarget(w, range, 2.10, dx, dz);          // ~120 deg cone
    if (t < 0) t = pickTarget(w, range * 0.55, Math.PI, dx, dz); // anything close

    if (t >= 0) {
      const yx = e.x[t] - e.x[id], yz = e.z[t] - e.z[id];
      e.yaw[id] = Math.atan2(yx, yz);
      e.target[id] = t;
    } else {
      e.yaw[id] = Math.atan2(dx, dz);
      e.target[id] = -1;
    }
    e.yawGoal[id] = e.yaw[id];
  }

  /** The player is the sole gameplay authority for lock-on target selection. */
  function validLock(w, target, range = LOCK_RANGE) {
    const e = w.entities, id = st.id;
    if (target < 0 || target >= e.capacity || target === id || !e.alive[target]) return false;
    if (e.state[target] === ST.DEAD || e.team[target] === e.team[id] || e.team[target] === TEAM.NEUTRAL) return false;
    const dx = e.x[target] - e.x[id], dz = e.z[target] - e.z[id];
    return dx * dx + dz * dz <= range * range;
  }

  function setLock(w, target) {
    const e = w.entities, id = st.id;
    const t = target == null ? -1 : target | 0;
    if (!validLock(w, t)) {
      st.locked = false; st.lockId = -1;
      if (id >= 0) e.target[id] = -1;
      return -1;
    }
    st.locked = true; st.lockId = t; e.target[id] = t;
    return t;
  }

  function toggleLock(w) {
    if (st.locked) return setLock(w, -1);
    const e = w.entities, id = st.id;
    const moving = st.wantX * st.wantX + st.wantZ * st.wantZ > 1e-4;
    const dx = moving ? st.wantX : Math.sin(e.yaw[id]);
    const dz = moving ? st.wantZ : Math.cos(e.yaw[id]);
    return setLock(w, pickTarget(w, 22, Math.PI, dx, dz));
  }

  /* ------------------------------------------------------------ move drive */

  function moveById(id) {
    if (!id) return null;
    return ms.moves[id] || null;
  }

  function startMove(w, moveId) {
    const m = moveById(moveId);
    if (!m) return false;
    const e = w.entities, id = st.id;

    if (st.mode === 'attack') w.combat?.endSwing?.(id);
    if (st.musouPhase === 'finish') endMusou(w);
    autoFace(w, Math.max(5.5, m.arc.r1 + 2.4));

    st.mode = 'attack';
    st.move = m; st.moveId = moveId; st.moveT = 0; st.moveF = 0;
    st.phase = 'windup'; st.canCancel = false;
    rootPrev = 0; extraFired = 0;
    st.dbg.lastMove = moveId;

    // chain bookkeeping — where the NEXT light/charge input branches from
    const ci = ms.chain.indexOf(moveId);
    if (ci >= 0) st.combo = (ci + 1 >= ms.chain.length) ? 0 : ci + 1;
    else if (moveId === ms.dash) st.combo = 1;
    else if (ms.charges.indexOf(moveId) >= 0) st.combo = 0;   // a charge ends the chain
    st.comboT = CFG.combat.comboWindow + m.total * F;

    e.state[id] = ST.WINDUP; e.stateT[id] = 0;
    e.vx[id] = 0; e.vz[id] = 0;
    st.speed = 0; e.speed[id] = 0;

    const lock = m.total * F + 0.06;
    w.anim?.play?.(id, m.clip, _play(m.rate, 0.07, lock, lock));
    w.combat?.beginSwing?.(id, m, { team: e.team[id] });
    if (m.invuln > 0) { st.invulnT = Math.max(st.invulnT, m.invuln); }
    w.bus.emit('player:move', moveId, m);
    return true;
  }

  function endMove(w) {
    const e = w.entities, id = st.id;
    w.combat?.endSwing?.(id);
    st.move = null; st.moveId = null; st.phase = null; st.canCancel = false;
    st.mode = 'free';
    e.state[id] = ST.IDLE; e.stateT[id] = 0;
    w.anim?.stop?.(id);
    if (st.musouPhase) endMusou(w);
  }

  /** Spread `move.root.d` metres across frames [a, b] with the authored ease. */
  function applyRoot(w, m, f) {
    const R = m.root;
    if (!(R.d > 0) || R.b <= R.a) return;
    const fn = ease[R.ease] || ease.outQuint;
    const p = fn(clamp01((f - R.a) / (R.b - R.a)));
    const step = (p - rootPrev) * R.d;
    rootPrev = p;
    if (step === 0) return;
    const e = w.entities, id = st.id;
    const H = CFG.world.half - 8;
    e.x[id] = clamp(e.x[id] + Math.sin(e.yaw[id]) * step, -H, H);
    e.z[id] = clamp(e.z[id] + Math.cos(e.yaw[id]) * step, -H, H);
  }

  /** Build (once) and fire a move-declared extra hitbox window. */
  function fireExtra(w, m, ov, i) {
    if (!ov._spec) {
      ov._spec = {
        arc: ov.arc || m.arc,
        damage: ov.damage != null ? ov.damage : m.damage,
        poise: ov.poise != null ? ov.poise : m.poise,
        knock: ov.knock != null ? ov.knock : m.knock,
        drag: ov.drag != null ? ov.drag : m.drag,
        lift: ov.lift != null ? ov.lift : m.lift,
        launch: ov.launch != null ? ov.launch : m.launch,
        radial: ov.radial != null ? ov.radial : m.radial,
        stun: ov.stun != null ? ov.stun : m.stun,
        gb: m.gb,
        hitstop: ov.hitstop || m.hitstop,
        shake: ov.shake != null ? ov.shake : m.shake,
        kick: ov.kick != null ? ov.kick : m.kick,
        hitCap: m.hitCap,
        vfx: ov.vfx || m.vfx, sfx: m.sfx,
        presentation: ov.presentation ?? m.presentation,
      };
    }
    w.combat?.pulse?.(st.id, ov._spec);
    extraFired |= (1 << i);
  }

  /* ------------------------------------------------------------- reactions */

  function enterDead(w) {
    const e = w.entities, id = st.id;
    w.combat?.endSwing?.(id);
    aura?.stop?.(); aura = null;
    slowUntil = 0; w.time.slowmo = 1;
    kvx = 0; kvz = 0; rootPrev = 0; extraFired = 0;

    st.mode = 'dead'; st.phase = 'dead';
    st.move = null; st.moveId = null; st.moveT = 0; st.moveF = 0; st.canCancel = false;
    st.buffer = null; st.bufferT = 0; st.combo = 0; st.comboT = 0;
    st.chain = 0; st.chainT = 0;
    st.hp = 0; st.maxHp = e.maxHp[id]; st.musouReady = false;
    st.guarding = false; st.guardBreakT = 0; st.sprinting = false;
    st.airborne = false; st.airH = 0; st.vy = 0; st.airCombo = 0;
    st.invulnT = 0; st.hurtT = 0; st.regenT = 0; st.musouPhase = null; st.musouT = 0;
    st.musouCycle = 0; st.musouPulse = 0;
    st.speed = 0; st.wantX = 0; st.wantZ = 0;
    setLock(w, -1);

    e.hp[id] = 0; e.vx[id] = 0; e.vy[id] = 0; e.vz[id] = 0; e.speed[id] = 0;
    e.state[id] = ST.DEAD; e.prevState[id] = ST.DEAD; e.stateT[id] = 0;
    e.fade[id] = 1; e.stagger[id] = 0;
    e.setFlag(id, FLAG.HYPER | FLAG.INVULN | FLAG.SUPERARMOR | FLAG.RAGDOLL | FLAG.JUGGLED, false);
    w.camrig?.setMode?.('follow');
  }

  /** Restore every player-owned public and private field for a fresh battle. */
  function resetBattle(w) {
    const e = w.entities, id = st.id;
    if (id < 0 || id >= e.capacity) return false;

    w.combat?.endSwing?.(id);
    aura?.stop?.(); aura = null;
    w.time.slowmo = 1; slowUntil = 0;
    kvx = 0; kvz = 0; rootPrev = 0; extraFired = 0; wired = false;
    const L = st.loadout;
    ms = MOVESETS[L.weapon] || MOVESETS.greatGlaive;

    const sx = L.spawn ? L.spawn.x : 0, sz = L.spawn ? L.spawn.z : 30;
    const walkY = w.terrain?.walkHeightAt;
    const y = w.terrain ? (walkY ? walkY.call(w.terrain, sx, sz) : w.terrain.heightAt(sx, sz)) : 0;
    e.alive[id] = 1;
    e.kind[id] = KIND.PLAYER; e.team[id] = TEAM.PLAYER;
    e.x[id] = sx; e.y[id] = y; e.z[id] = sz;
    e.vx[id] = 0; e.vy[id] = 0; e.vz[id] = 0;
    e.yaw[id] = 0; e.yawGoal[id] = 0; e.pitch[id] = 0; e.roll[id] = 0;
    e.hp[id] = P.maxHp; e.maxHp[id] = P.maxHp;
    e.radius[id] = P.radius; e.height[id] = P.height; e.scale[id] = 1.06; e.mass[id] = 2.2;
    e.state[id] = ST.IDLE; e.prevState[id] = ST.IDLE; e.stateT[id] = 0;
    e.anim[id] = 0; e.prevAnim[id] = 0;
    e.animT[id] = 0; e.animSpeed[id] = 1; e.animBlend[id] = 1;
    e.stagger[id] = 0; e.maxPoise[id] = 400; e.poise[id] = 400;
    e.cd[id] = 0; e.aiT[id] = 0; e.think[id] = 0; e.speed[id] = 0;
    e.fade[id] = 1; e.spawnT[id] = 0; e.flash[id] = 0; e.morale[id] = 0.5;
    e.target[id] = -1; e.flags[id] = 0; e.group[id] = -1; e.hitToken[id] = 0;
    e.archetype[id] = 0; e.variant[id] = 0; e.ext[id] = null;

    st.hp = P.maxHp; st.maxHp = P.maxHp;
    st.musou = 0; st.maxMusou = P.maxMusou; st.musouReady = false;
    st.bravery = 0; st.maxBravery = braveryCapFor(W?.progress?.rank || 1);
    st.braverySpent = 0; st.braveryEarned = 0; st.braveryDenied = 0;
    st.guardT = 0; st.parries = 0; st.perfectDodges = 0; st.braveryFromDefence = 0;
    st.mountSpeed = 1; st.mountAccel = 1; st.mounted = false;  // a new battle starts on foot
    st.wading = 0;
    st.guardHp = 100; st.maxGuardHp = 100;
    st.mode = 'free'; st.weapon = L.weapon; st.moveset = ms; st.allowDeath = true;
    st.name = L.name; st.title = L.title;
    st.moveId = null; st.move = null; st.phase = null;
    st.moveT = 0; st.moveF = 0; st.canCancel = false;
    st.combo = 0; st.comboT = 0; st.chain = 0; st.chainT = 0; st.maxChain = 0;
    st.kills = 0; st.hits = 0;
    st.lockId = -1; st.locked = false;
    st.guarding = false; st.guardBreakT = 0; st.sprinting = false;
    st.airborne = false; st.airH = 0; st.vy = 0; st.airCombo = 0;
    st.invulnT = 0; st.hurtT = 0; st.regenT = 0;
    st.musouT = 0; st.musouPhase = null; st.musouCycle = 0; st.musouPulse = 0;
    st.buffer = null; st.bufferT = 0; st.speed = 0; st.wantX = 0; st.wantZ = 0;
    st.dbg.lastMove = ''; st.dbg.lastHits = 0; st.dbg.bestSweep = 0;
    st.dbg.musouPulseSweeps = 0; st.dbg.musouPulseHits = 0;

    w.anim?.stop?.(id);
    return true;
  }

  /** Called by combat when a blow lands on the hero. */
  st.hurt = function (amount, ux, uz, knock, lift, killed) {
    const w = W; if (!w) return;
    const e = w.entities, id = st.id;
    st.musou = Math.min(st.maxMusou, st.musou + amount * 0.9);
    st.regenT = REGEN_DELAY;
    if (killed) {
      // Combat normally caps the damage before this point. This second gate
      // makes direct/legacy hurt calls obey the same one authoritative policy.
      if (!st.allowDeath) {
        e.hp[id] = Math.max(1, e.hp[id]); st.hp = e.hp[id];
        e.state[id] = ST.IDLE; e.prevState[id] = ST.IDLE; e.stateT[id] = 0;
        st.mode = 'free'; st.phase = null;
        return;
      }
      enterDead(w);
      return;
    }
    if (st.mode === 'musou') return;                 // invulnerable, never staggers

    /* HYPER-ARMOUR. A peon's blow chips health and rattles the pose; it does
     * NOT take the hero off his feet. The AI classifies its own blows for us —
     * `knock` arrives as 0.6 for a peon swing, 2 for an officer's finisher and
     * 4 for a guard break — so the test is free. His poise pool (400) still
     * drains, and a mob thick enough to land ~4 blows a second does eventually
     * break it, which is the only thing that can stagger him from a crowd. */
    const hard = knock >= 1.5 || lift > 0.4 || amount >= 55;
    e.poise[id] = Math.max(0, e.poise[id] - amount * 8);
    const broke = e.poise[id] <= 0;
    st.invulnT = Math.max(st.invulnT, HURT_GRACE);
    if (!hard && !broke) return;                     // chipped, not staggered

    e.poise[id] = e.maxPoise[id];
    st.chain = 0; st.chainT = 0;
    if (st.mode === 'attack') w.combat?.endSwing?.(id);
    st.mode = 'hurt';
    st.hurtT = amount > 55 ? 0.52 : 0.30;
    e.stagger[id] = amount > 55 ? 0.9 : 0.2;
    kvx = ux * knock * 2.6; kvz = uz * knock * 2.6;
    e.state[id] = ST.HIT; e.stateT[id] = 0;
    st.move = null; st.moveId = null; st.phase = null;
  };

  /** Called by combat every time one of the hero's sweeps connects. */
  st.onHits = function (n) {
    st.hits += n;
    st.chain += n; st.chainT = 2.4;
    if (st.chain > st.maxChain) st.maxChain = st.chain;
    // A packed C4 lands ~25 hits; at 7 a head that filled the gauge in one
    // swing, which makes the musou free. 3 a hit + 6 a KO puts a full gauge at
    // roughly two full C1-C6 chains through a crowd.
    st.musou = Math.min(st.maxMusou, st.musou + n * 3);
    st.gainBravery(n * BRAVERY.perHit);
    if (n > st.dbg.bestSweep) st.dbg.bestSweep = n;
    st.dbg.lastHits = n;
  };

  /* -------------------------------------------------------------- bravery */

  /**
   * Earn. Capped, never negative, and it tracks the TOTAL earned separately so
   * a probe can tell "the pool is full because nothing was spent" apart from
   * "the pool is full because nothing was earned" — two states that look
   * identical from the gauge and mean opposite things about the fight.
   */
  st.gainBravery = function (n) {
    if (!(n > 0)) return 0;
    const before = st.bravery;
    st.bravery = Math.min(st.maxBravery, st.bravery + n);
    st.braveryEarned += n;
    return st.bravery - before;
  };

  /* ---- the two defensive windows (DEV_PLAN Step 4) --------------------- *
   * Both are read by src/combat/combat.js, which is where the blow actually
   * arrives; keeping the TEST here means the rule lives with the state it is
   * about, and combat only has to ask. */

  /** True for the first frames of a guard — a blow arriving now is parried. */
  Object.defineProperty(st, 'parryWindowOpen', {
    get() { return st.mode === 'guard' && st.guardT <= DEFENCE.parryWindow; },
  });

  /** True for the first frames of a dodge — a blow that would have connected
   *  now is a perfect dodge rather than simply a miss. */
  Object.defineProperty(st, 'dodgeWindowOpen', {
    get() { return st.mode === 'dodge' && st.moveT <= DEFENCE.dodgeWindow; },
  });

  /**
   * A blow was read at the start of a guard. Costs the attacker, pays the
   * defender, and takes NO chip damage — the difference between this and an
   * ordinary block is the whole reward for the read.
   */
  st.onParry = function (srcId) {
    const w = W; if (!w) return 0;
    const e = w.entities, id = st.id;
    st.parries++;
    const got = st.gainBravery(BRAVERY.perParry);
    st.braveryFromDefence += got;
    st.invulnT = Math.max(st.invulnT, DEFENCE.parryIFrames);
    // the attacker pays, through the documented path rather than by poking
    // at AI state this module does not own
    if (srcId >= 0) w.ai?.stagger?.(srcId, DEFENCE.parryStagger, 1);
    const fx = Math.sin(e.yaw[id]), fz = Math.cos(e.yaw[id]);
    /* 'critical', not a new 'parry' kind: src/combat/impact.js maps kinds onto
     * authored spark defs through SPARK_KIND, and a name that is not in that
     * table looks up undefined. A parry reads as a crit — bright and sharp —
     * which is both apt and already authored. */
    w.impact?.hit?.('critical', e.x[id] + fx * 0.6, e.y[id] + 1.25, e.z[id] + fz * 0.6, fx, 0.3, fz, 1.0);
    w.audio?.play?.('guard', _snd(e.x[id], e.y[id] + 1.25, e.z[id], 1.25));
    w.ui?.toast?.('Parry');
    w.bus.emit('player:defence', 'parry', srcId, got);
    return got;
  };

  /**
   * A hitbox that would genuinely have connected passed through the opening
   * frames of a dodge. Detected in combat.js from the arc geometry, so this is
   * never a guess about intent — the blow was on target.
   */
  st.onPerfectDodge = function (srcId) {
    const w = W; if (!w) return 0;
    const e = w.entities, id = st.id;
    st.perfectDodges++;
    const got = st.gainBravery(BRAVERY.perPerfectDodge);
    st.braveryFromDefence += got;
    /* No impact spark: a perfect dodge is a blow that MISSED, and stamping a
     * hit flash on it would say the opposite. A ring at the feet reads as the
     * evade it is. */
    w.vfx?.ring?.(e.x[id], e.y[id] + 0.08, e.z[id], _ring);
    w.ui?.toast?.('Perfect Dodge');
    w.bus.emit('player:defence', 'dodge', srcId, got);
    return got;
  };

  /** Can this verb be paid for right now? */
  function canAfford(cost) { return !(cost > 0) || st.bravery >= cost - 1e-6; }

  /** Pay, or refuse and record the refusal. */
  function spendBravery(cost) {
    if (!(cost > 0)) return true;
    if (st.bravery < cost - 1e-6) { st.braveryDenied++; return false; }
    st.bravery -= cost;
    if (st.bravery < 0) st.bravery = 0;
    st.braverySpent += cost;
    return true;
  }

  /* ---------------------------------------------------------------- musou */

  function canMusou() { return st.musou >= st.maxMusou * ms.musou.cost - 0.5; }

  function startMusou(w) {
    const e = w.entities, id = st.id;
    if (st.mode === 'attack') w.combat?.endSwing?.(id);
    st.mode = 'musou'; st.musouPhase = 'grind';
    st.musouT = 0; st.musouCycle = 0; st.musouPulse = 0;
    st.move = null; st.moveId = null; st.phase = 'musou';
    e.setFlag(id, FLAG.INVULN, true);
    e.setFlag(id, FLAG.HYPER, true);
    e.state[id] = ST.ATTACK; e.stateT[id] = 0;
    const M = ms.musou;
    const lock = w.anim?.duration?.(M.clip) / M.rate;
    w.anim?.play?.(id, M.clip, _play(M.rate, 0.1, lock, lock));
    st.musouCycle = lock;
    w.camrig?.setMode?.('musou');
    w.camrig?.shake?.(0.4, 14, 0.5);
    // One release silhouette belongs to the state transition, never to the
    // repeating grinder pulse. VFX owns particles only; the camera/post beat
    // above and below remains a single gameplay-owned cue.
    _vopt.kind = 'musou'; _vopt.power = 0.72; _vopt.scale = 0.9;
    _vopt.element = 'none'; _vopt.radius = 5.5; _vopt.life = 0.55; _vopt.flash = false;
    w.vfx?.burst?.(e.x[id], e.y[id] + 1.0, e.z[id], _vopt);
    // Measured in the playthrough: at intensity 1.15 the aura's flame column,
    // its seven 5 m light shafts and the per-pulse burst together bleach the
    // whole frame — for the musou's full four seconds the hero, the bodies and
    // the battlefield are all invisible behind a white disc. Two thirds of the
    // emission still reads as a storm and leaves an image underneath it.
    aura = w.vfx?.aura?.(id, { element: 'none', seconds: M.duration + 1.6, intensity: 0.62, scale: 0.8 }) || null;
    w.audio?.play?.('musou_start', _snd(e.x[id], e.y[id] + 1.4, e.z[id], 1));
    w.pipeline?.radialBlur?.(0.18);
    w.ui?.toast?.('MUSOU');
    w.bus.emit('player:musou', 'start');
  }

  function stepMusou(w, dt) {
    const e = w.entities, id = st.id;
    const M = ms.musou;
    st.musouT += dt;

    if (st.musouPhase === 'grind') {
      // gauge burns down across the whole flurry
      st.musou = Math.max(0, st.musou - (st.maxMusou * M.cost / M.duration) * dt);
      // the body spins; the hero drifts on the stick
      e.yaw[id] = wrapPi(e.yaw[id] + M.spin * (Math.PI / 180) * dt);
      const len = Math.hypot(st.wantX, st.wantZ);
      const spd = len > 0.1 ? M.moveSpeed : 0;
      e.vx[id] = st.wantX * spd; e.vz[id] = st.wantZ * spd;
      const H = CFG.world.half - 8;
      e.x[id] = clamp(e.x[id] + e.vx[id] * dt, -H, H);
      e.z[id] = clamp(e.z[id] + e.vz[id] * dt, -H, H);
      e.speed[id] = 0;

      // re-trigger the spin clip when it runs out
      st.musouCycle -= dt;
      if (st.musouCycle <= 0) {
        const lock = w.anim.duration(M.clip) / M.rate;
        w.anim.play(id, M.clip, _play(M.rate, 0.05, lock, lock));
        st.musouCycle = lock;
      }

      // the grinder: one 360 arc every `pulse.every` frames
      st.musouPulse += 1;
      if (st.musouPulse >= M.pulse.every) {
        st.musouPulse = 0;
        const hits = w.combat?.pulse?.(id, M.pulse) || 0;
        st.dbg.musouPulseSweeps++;
        st.dbg.musouPulseHits += hits;
      }
      if (st.musouT >= M.duration) {
        // hand over to the ordinary attack pipeline; `musouPhase` keeps the
        // i-frames on and makes endMove() close the musou down.
        st.musouPhase = null;
        startMove(w, M.finish);
        st.musouPhase = 'finish';
      }
      return;
    }
    // never reached: once the finisher starts, mode is 'attack'
    st.mode = 'free';
  }

  function endMusou(w) {
    const e = w.entities, id = st.id;
    e.setFlag(id, FLAG.INVULN, false);
    e.setFlag(id, FLAG.HYPER, false);
    st.musouPhase = null; st.musouT = 0;
    st.musou = 0;
    st.mode = 'free';
    w.camrig?.setMode?.('follow');
    w.pipeline?.radialBlur?.(0);
    aura?.stop?.(); aura = null;
    w.bus.emit('player:musou', 'end');
  }

  /* ----------------------------------------------------------- the buffer */

  function readBuffer(w, dt) {
    const inp = w.input;
    const win = CFG.combat.inputBuffer;
    // priority order: the biggest intent wins if two land on the same frame
    for (let i = 0; i < BUFFER_ORDER.length; i++) {
      const n = BUFFER_ORDER[i] === 'jump' ? 'special' : BUFFER_ORDER[i];
      if (inp.consume(n, win)) { st.buffer = BUFFER_ORDER[i]; st.bufferT = win; break; }
    }
    if (st.bufferT > 0) { st.bufferT -= dt; if (st.bufferT <= 0) st.buffer = null; }
  }

  /** Which light attack does the current situation want? */
  function lightId() {
    if (st.airborne) return ms.air[Math.min(st.airCombo, ms.air.length - 1)];
    if (st.sprinting && st.combo === 0 && st.speed > P.runSpeed * 0.85) return ms.dash;
    const i = Math.min(st.combo, ms.chain.length - 1);
    return ms.chain[i];
  }
  function chargeId() {
    if (st.airborne) return ms.airHeavy;
    const i = Math.min(st.combo, ms.charges.length - 1);
    return ms.charges[i];
  }

  /** Try to spend the buffered input. `strict` gates the light/charge chain. */
  function spendBuffer(w, allowAttack, allowDodge, allowMusou) {
    const b = st.buffer;
    if (!b) return false;
    if (b === 'musou') {
      if (!allowMusou || !canMusou()) return false;
      st.buffer = null; startMusou(w); return true;
    }
    if (b === 'dodge') {
      if (!allowDodge) return false;
      st.buffer = null; startDodge(w); return true;
    }
    if (b === 'jump') {
      if (!allowAttack || st.airborne) return false;
      st.buffer = null; jump(w); return true;
    }
    if (!allowAttack) return false;

    /* HEAVY IS NO LONGER FREE (DEV_PLAN Step 3).
     *
     * Heavy while GUARDING is the Battle Art — three bravery for the musou
     * finisher on its own, which is where a burst tier belongs: above a
     * charge, below a musou. Heavy otherwise is a charge branch at one.
     *
     * An air heavy stays free. It already costs a jump and the airborne
     * chain is the one place the player has no guard, no dodge and no
     * alternative, so pricing it would only make being in the air worse. */
    let id, cost = 0;
    if (b === 'heavy') {
      if (st.guarding && !st.airborne) {
        const art = ms.battleArt;
        if (!art || !art.id) return false;
        id = art.id; cost = art.bravery || 0;
      } else {
        id = chargeId();
        /* READ THE PRICE OFF THE MOVE, not off BRAVERY.charge.
         *
         * This line used to charge the flat constant, which meant the per-move
         * price the moveset stamps was decorative at the only moment it
         * mattered: the contract test read `mv.bravery` and the GAME read
         * something else, so the two could disagree indefinitely and the test
         * would still pass. It is also why all five branches cost the same
         * whatever the table said. */
        const cm = st.moveset.moves[id];
        cost = st.airborne ? 0 : (cm && cm.bravery > 0 ? cm.bravery : BRAVERY.charge);
      }
    } else {
      id = lightId();
    }
    if (!id) return false;
    /* A refused verb must NOT eat the buffer: the player pressed heavy, and if
     * it cannot be paid for they should get the light they are still holding
     * rather than a dropped input and a dead frame. */
    if (!spendBravery(cost)) {
      if (b !== 'heavy') return false;
      st.buffer = null;
      id = lightId();
      if (!id) return false;
      if (st.airborne) st.airCombo++;
      return startMove(w, id);
    }
    st.buffer = null;
    if (st.airborne) st.airCombo++;
    return startMove(w, id);
  }

  /* ---------------------------------------------------------------- dodge */

  function startDodge(w) {
    const e = w.entities, id = st.id;
    if (st.mode === 'attack') w.combat?.endSwing?.(id);
    const D = COMMON.dodge;
    st.mode = 'dodge'; st.moveT = 0; st.moveF = 0; rootPrev = 0;
    st.move = null; st.moveId = 'dodge'; st.phase = 'dodge';
    st.combo = 0; st.guarding = false;
    // roll in the stick direction; with no stick, roll where he is looking
    const len = Math.hypot(st.wantX, st.wantZ);
    if (len > 0.15) e.yaw[id] = Math.atan2(st.wantX, st.wantZ);
    e.yawGoal[id] = e.yaw[id];
    e.state[id] = ST.DODGE; e.stateT[id] = 0;
    e.vx[id] = 0; e.vz[id] = 0; e.speed[id] = 0;
    st.invulnT = D.invuln;
    const lock = D.dur + 0.05;
    w.anim?.play?.(id, D.clip, _play(D.rate, 0.06, lock, lock));
    w.audio?.play?.('roll', _snd(e.x[id], e.y[id] + 0.6, e.z[id], 1));
    _vopt.kind = 'slide'; w.vfx?.dust?.(e.x[id], e.y[id] + 0.06, e.z[id], 0.5, _vopt);
  }

  function stepDodge(w, dt) {
    const e = w.entities, id = st.id;
    const D = COMMON.dodge;
    st.moveT += dt;
    const p = ease.outQuart(clamp01(st.moveT / (D.dur * 0.82)));
    const step = (p - rootPrev) * D.dist;
    rootPrev = p;
    const H = CFG.world.half - 8;
    e.x[id] = clamp(e.x[id] + Math.sin(e.yaw[id]) * step, -H, H);
    e.z[id] = clamp(e.z[id] + Math.cos(e.yaw[id]) * step, -H, H);

    if (st.moveT >= D.cancelAt) spendBuffer(w, true, false, true);
    if (st.mode !== 'dodge') return;
    if (st.moveT >= D.dur) { st.mode = 'free'; st.moveId = null; st.phase = null; e.state[id] = ST.IDLE; w.anim?.stop?.(id); }
  }

  /* ----------------------------------------------------------------- jump */

  function jump(w) {
    const e = w.entities, id = st.id;
    st.airborne = true; st.vy = COMMON.jump.up; st.airH = 0.02; st.airCombo = 0;
    e.state[id] = ST.AIRBORNE; e.stateT[id] = 0;
    _vopt.kind = 'land'; w.vfx?.dust?.(e.x[id], e.y[id] + 0.05, e.z[id], 0.6, _vopt);
  }

  function stepAir(w, dt) {
    const e = w.entities, id = st.id;
    st.vy += COMMON.jump.gravity * dt;
    st.airH += st.vy * dt;
    if (st.airH <= 0) {
      st.airH = 0; st.vy = 0; st.airborne = false; st.airCombo = 0;
      if (st.mode === 'free') { e.state[id] = ST.IDLE; w.anim?.play?.(id, 'land_hard', _play(1, 0.06, 0.4, 0.34)); }
      w.vfx?.land?.(e.x[id], e.y[id], e.z[id], 0.9);
      w.impact?.hit?.('land', e.x[id], e.y[id], e.z[id], 0, 1, 0, 0.35);
    }
  }

  /* ================================================================ SYSTEM */

  return {
    name: 'player', order: ORDER.PLAYER,

    init(w) {
      W = w;
      const e = w.entities;
      const y = w.terrain ? w.terrain.heightAt(0, 30) : 0;
      const id = e.spawn(KIND.PLAYER, TEAM.PLAYER, 0, y, 30);
      w.playerId = id; st.id = id;
      // Boot and retry share the exact same initializer, including entity
      // history fields (anim/prevAnim and state/prevState).
      resetBattle(w);

      /* --- public surface ------------------------------------------------ */
      st.moveset = ms;
      /**
       * Switch weapon: the MOVESET and the MODEL, together.
       *
       * These used to be two unlinked APIs — this one swapped the moveset and
       * `chars.setPlayerWeapon()` swapped the mesh, and the only caller of the
       * second was the front end's roster row. So every other route into a
       * weapon change, `setLoadout()` included, left the hero holding whatever
       * he had before: a solo capture of the charge branch photographed
       * fifteen "twin sabres" poses in which he is swinging a polearm.
       * One call, one truth.
       */
      st.setWeapon = (name) => {
        if (!MOVESETS[name]) return false;
        ms = MOVESETS[name]; st.weapon = name; st.moveset = ms; st.combo = 0;
        try { w.chars?.setPlayerWeapon?.(ms.weapon, { offhand: ms.offhand || null }); }
        catch (e) { /* chars not built yet; the roster path re-arms on boot */ }
        return true;
      };
      /**
       * The front end's committed choice: which warrior, and where the chosen
       * stage drops him. Applied immediately AND remembered, because
       * `resetBattle()` runs on every mission restart and would otherwise
       * re-arm the default glaive at the default spawn.
       */
      st.setLoadout = (o) => {
        const L = st.loadout;
        if (o && o.weapon && MOVESETS[o.weapon]) { L.weapon = o.weapon; st.setWeapon(o.weapon); }
        if (o && o.name) { L.name = o.name; st.name = o.name; }
        if (o && o.title) { L.title = o.title; st.title = o.title; }
        if (o && o.spawn) L.spawn = { x: o.spawn.x, z: o.spawn.z };
        return L;
      };
      st.nearestThreat = (range = 14) => pickTarget(w, range, Math.PI, 0, 1);
      st.faceTarget = (t) => { if (t >= 0) { const E = w.entities; E.yaw[st.id] = Math.atan2(E.x[t] - E.x[st.id], E.z[t] - E.z[st.id]); E.yawGoal[st.id] = E.yaw[st.id]; } };
      st.attack = (moveId) => startMove(w, moveId);
      st.addMusou = (v) => { st.musou = clamp(st.musou + v, 0, st.maxMusou); };
      st.fillMusou = () => { st.musou = st.maxMusou; };
      st.forceMusou = () => { if (st.mode !== 'musou') { st.musou = st.maxMusou; startMusou(w); return true; } return false; };
      st.canMusou = canMusou;
      st.moveList = () => Object.keys(ms.moves);
      st.setLock = (target) => setLock(w, target);
      st.toggleLock = () => toggleLock(w);
      st.validateLock = (target = st.lockId, range = LOCK_RANGE) => validLock(w, target, range);
      st.setAllowDeath = (on) => {
        st.allowDeath = !!on;
        if (!st.allowDeath && e.state[id] !== ST.DEAD) {
          e.hp[id] = Math.max(1, e.hp[id]); st.hp = e.hp[id];
        }
        return st.allowDeath;
      };
      st.canDie = () => st.allowDeath;
      st.resetBattle = () => resetBattle(w);
      st.die = (src = -1) => {
        if (e.state[id] === ST.DEAD) return false;
        if (!st.allowDeath) {
          e.hp[id] = Math.max(1, e.hp[id]); st.hp = e.hp[id];
          return false;
        }
        enterDead(w);
        w.bus.emit('kill', id, src);
        return true;
      };

      st.canAfford = canAfford;
      /* The cost model, reachable from a running world. The cap CURVE and the
       * promise that no weapon ships a free charge are arithmetic a trace
       * cannot see, so the contract test checks them directly. */
      st.braveryCapFor = braveryCapFor;
      st.allMovesets = () => MOVESET_LIST;

      w.player = st;

      w.bus.on('kill', (victim, src) => { if (src === id) st.musou = Math.min(st.maxMusou, st.musou + 6); });

      /* Holding guard through a blow pays bravery. combat.js emits `guarded`
       * with the GUARDER's id, so this needs no knowledge of who swung.
       * Defence has never paid for anything in this game before; this is the
       * first line that makes standing in a blow worth doing. */
      w.bus.on('guarded', (who) => { if (who === id) st.gainBravery(BRAVERY.perGuard); });

      /* The cap follows career rank, and rank can climb mid-battle, so it is
       * re-read rather than fixed at spawn. Widening the pool never spills
       * what is already in it. */
      w.bus.on('mission:start', () => { st.maxBravery = braveryCapFor(w.progress?.rank || 1); });
      w.bus.on('progress:rank', (r) => { st.maxBravery = braveryCapFor(r); });
    },

    fixedUpdate(w, dt) {
      const e = w.entities, id = st.id;
      if (id < 0) return;

      // The animator's own root translation is switched off so the moveset's
      // declared lunge is the only thing that moves the body (docs/REQUESTS.md).
      if (!wired && w.anim) { wired = true; w.anim.applyRootMotion = false; }
      if (slowUntil > 0 && w.time.real >= slowUntil) { slowUntil = 0; w.time.slowmo = 1; }

      // Death is terminal until mission.reset() calls resetBattle(). Keeping the
      // corpse in this slot preserves camera/HUD ownership through the result.
      if (e.state[id] === ST.DEAD) {
        if (!st.allowDeath) {
          e.hp[id] = Math.max(1, e.hp[id]); st.hp = e.hp[id];
          e.state[id] = ST.IDLE; e.prevState[id] = ST.IDLE; e.stateT[id] = 0;
          st.mode = 'free'; st.phase = null;
        } else {
          if (st.mode !== 'dead') enterDead(w);
          st.hp = 0; st.maxHp = e.maxHp[id]; e.fade[id] = 1; e.stateT[id] += dt;
          return;
        }
      }

      /* ---- timers --------------------------------------------------- */
      if (st.comboT > 0) { st.comboT -= dt; if (st.comboT <= 0) { st.comboT = 0; st.combo = 0; } }
      if (st.chainT > 0) { st.chainT -= dt; if (st.chainT <= 0) { st.chainT = 0; st.chain = 0; } }
      if (st.invulnT > 0) { st.invulnT -= dt; if (st.invulnT <= 0) st.invulnT = 0; }
      if (st.guardBreakT > 0) st.guardBreakT -= dt;
      e.setFlag(id, FLAG.INVULN, st.invulnT > 0 || st.mode === 'musou' || st.musouPhase !== null);
      if (st.mode !== 'guard' && st.guardHp < st.maxGuardHp) st.guardHp = Math.min(st.maxGuardHp, st.guardHp + 26 * dt);
      st.musou = Math.min(st.maxMusou, st.musou + P.musouRegen * dt);
      st.musouReady = canMusou();
      // out-of-contact recovery: the bar comes back once he breaks off.
      // `st.hp` still holds last step's value here, so this catches EVERY
      // source — including chip damage through a guard, which never reaches
      // st.hurt().
      if (e.hp[id] < st.hp - 0.01) st.regenT = REGEN_DELAY;
      if (st.regenT > 0) st.regenT -= dt;
      else if (e.hp[id] < e.maxHp[id] && e.state[id] !== ST.DEAD) {
        e.hp[id] = Math.min(e.maxHp[id], e.hp[id] + e.maxHp[id] * REGEN_RATE * dt);
      }
      st.hp = e.hp[id]; st.maxHp = e.maxHp[id];

      /* ---- input ----------------------------------------------------- */
      const inp = w.input;
      readBuffer(w, dt);
      // Move relative to the camera the player can SEE, not the one the rig is
      // heading towards.
      //
      // `camrig.yaw` is the BASE pose and updates the instant you move the
      // mouse. What is on screen is `camrig.solved.yaw`, which damps toward it
      // at rate 14 (src/camera/camera.js) -- a ~71 ms lag. Steering off the
      // base yaw meant that during every camera turn W went where the camera
      // was ABOUT to be while the screen still showed where it was, so the
      // hero visibly veered off-screen-forward for the length of the turn. On
      // a laptop, where the camera is being nudged constantly, that mismatch
      // is more or less permanent, and it reads as the controls being wrong.
      //
      // The exception is a scripted cinematic, which owns the pose outright and
      // sweeps hundreds of degrees: there the base yaw stays the honest basis,
      // so a flythrough cannot spin the controls out from under you.
      const cam = w.camrig;
      const yawCam = cam ? (cam.stats?.cinematic ? cam.yaw : (cam.solved?.yaw ?? cam.yaw)) : 0;
      const s = Math.sin(yawCam), c = Math.cos(yawCam);
      const mx = inp.move.x, my = inp.move.y;
      // dz is NEGATED against the obvious form, and it has to be.
      //
      // Measured three independent ways -- the camera's world matrix, the
      // camera-to-hero vector, and the rig's own solved yaw -- the camera sits
      // behind the hero at 180 deg and looks along +Z. `mx*s + my*c` sent W to
      // -Z, i.e. straight into the camera: forward and back were inverted while
      // strafing was fine, which is exactly how it felt to play.
      //
      // Why it survived so long: at yaw 0 the sine term drops out, so the error
      // is invisible unless the camera is turned. Every capture scenario starts
      // at yaw 0.
      //
      // BOTH axes are negated, and the determinant is why. Fixing only the
      // forward axis left a basis with determinant -1 -- a REFLECTION, which is
      // exactly what a mirrored left/right is. I noticed that -1, called it
      // suspicious, and shipped it anyway because only forward/back had been
      // reported; playtesting then reported A and D reversed, precisely as the
      // determinant had predicted. This form is det +1, a proper rotation,
      // which is what a camera-relative basis must be.
      //
      // The tempting smaller fix -- flipping only the `my` term -- is wrong and
      // worse than the bug. Its determinant is cos(2Y), ZERO at yaw 45: W and D
      // collapse onto the same vector and diagonals stop existing.
      //
      // tools/steering-check.mjs asserts all four keys at yaw 0, 40 and -125.
      let dx = -mx * c - my * s, dz = mx * s - my * c;
      const len = Math.hypot(dx, dz);
      if (len > 1e-4) { st.wantX = dx / len; st.wantZ = dz / len; } else { st.wantX = 0; st.wantZ = 0; }
      st.sprinting = inp.held('sprint') && len > 0.6 && st.mode === 'free';

      if (inp.consume('lockon', 0.3)) toggleLock(w);
      if (st.locked && !validLock(w, st.lockId)) setLock(w, -1);
      if (st.locked) {
        const ddx = e.x[st.lockId] - e.x[id], ddz = e.z[st.lockId] - e.z[id];
        if (ddx * ddx + ddz * ddz > LOCK_RANGE * LOCK_RANGE) setLock(w, -1);
        else e.target[id] = st.lockId;
      }

      // Guard is a hold on its own key. It used to fall back to holding the
      // dodge button, which swallowed the dodge: the audio engineer noticed the
      // roll sound never fired because the input routed to guard instead.
      const wantGuard = st.guardBreakT <= 0 && (inp.held('guard') || inp.held('interact'));

      /* ---- state machine --------------------------------------------- */
      switch (st.mode) {
        case 'attack': stepMove(w, dt); break;
        case 'dodge': stepDodge(w, dt); break;
        case 'musou': stepMusou(w, dt); break;
        case 'hurt': {
          st.hurtT -= dt;
          const H = CFG.world.half - 8;
          e.x[id] = clamp(e.x[id] + kvx * dt, -H, H);
          e.z[id] = clamp(e.z[id] + kvz * dt, -H, H);
          kvx *= Math.exp(-6 * dt); kvz *= Math.exp(-6 * dt);
          e.speed[id] = 0;
          if (st.hurtT <= 0 && e.state[id] !== ST.DEAD) {
            st.mode = 'free'; e.state[id] = ST.IDLE; e.stagger[id] = 0; w.anim?.stop?.(id);
          }
          break;
        }
        case 'guard':
          st.guarding = true;
          st.guardT += dt;
          e.state[id] = ST.BLOCK;
          locomote(w, dt, 0.34);
          if (!wantGuard) { st.guarding = false; st.mode = 'free'; }
          else spendBuffer(w, true, true, true);
          break;
        default: {                                   // 'free'
          st.guarding = false;
          if (!spendBuffer(w, true, true, true)) {
            // guardT restarts with the guard, which is what makes the parry a
            // READ rather than something a held button collects for free
            if (wantGuard && !st.airborne) { st.mode = 'guard'; st.guarding = true; st.guardT = 0; }
            else locomote(w, dt, 1);
          }
          break;
        }
      }

      /* ---- walls ----------------------------------------------------- */
      // Every horizontal mover above — locomotion, a move's lunge, a dodge,
      // the musou drift, a hurt slide — clamps to the world square on its own.
      // The arena's walls are one clamp here, after all of them, so no mode
      // can walk the hero through masonry. Nothing moves him faster than
      // ~0.3 m a step and the thinnest wall is 3.2 m, so there is no tunnel.
      if (WALLS.n && pushOut(e.x[id], e.z[id], P.radius, _wp)) {
        e.x[id] = _wp.x; e.z[id] = _wp.z;
        const vn = e.vx[id] * _wp.nx + e.vz[id] * _wp.nz;
        if (vn < 0) { e.vx[id] -= vn * _wp.nx; e.vz[id] -= vn * _wp.nz; }
        const kn = kvx * _wp.nx + kvz * _wp.nz;
        if (kn < 0) { kvx -= kn * _wp.nx; kvz -= kn * _wp.nz; }
      }

      /* ---- vertical -------------------------------------------------- */
      if (st.airborne) stepAir(w, dt);
      // walkHeightAt, not heightAt: the bridge deck sits 5.2 m above the
      // heightfield and has no representation in it, so clamping to the
      // ground walked the player down the gorge and under the arches
      // (measured at the Brantas: y = -15.95 while standing 'on' the bridge).
      const gh = w.terrain ? (w.terrain.walkHeightAt ? w.terrain.walkHeightAt(e.x[id], e.z[id]) : w.terrain.heightAt(e.x[id], e.z[id])) : 0;
      e.y[id] = gh + st.airH;
      if (st.airborne && e.state[id] === ST.IDLE) e.state[id] = ST.AIRBORNE;

      st.phase = st.move ? st.phase : (st.mode === 'free' ? null : st.mode);
      e.stateT[id] += dt;
    },
  };

  /* ---------------------------------------------------------- locomotion -- */

  function locomote(w, dt, scale) {
    const e = w.entities, id = st.id;
    const stick = clamp01(w.input.moveLen || 0);
    let want = 0;
    if (stick > 0.02) {
      want = stick < 0.55
        ? P.walkSpeed * (stick / 0.55)
        : lerp(P.walkSpeed, st.sprinting ? P.sprintSpeed : P.runSpeed, (stick - 0.55) / 0.45);
    }
    want *= scale * st.mountSpeed;
    if (st.airborne) want *= 0.72;

    /* THE RIVER IS NOW AN OBSTACLE.
     *
     * Measured honestly the CROSSING is a ford — about 0.55 m of water over the
     * bed — so the ordinary case costs speed and not your footing. Where the
     * valley already dips, the same flat water line stands over much deeper
     * ground and pools past 3 m; that is over a man's head and does push. The earlier
     * claim here that it was "4.6 m of channel below its own water line" came
     * from a `waterAt()` that was reading the bank-top reference as the water
     * surface; it is corrected, and so is this.
     *
     * It is also the ONLY traversal cost on this map. The Step 7 mount probe
     * found every leg walking exactly straight-line-minus-arrival-radius at
     * efficiency 1.017 — the valley charged nothing but distance — and that is
     * what held the traversalCost rubric row at 4. This is the first thing on
     * the map a route has to bend around. */
    st.wading = 0; st.current = 0;
    if (!st.airborne && w.terrain && w.terrain.waterAt) {
      const wa = w.terrain.waterAt(e.x[id], e.z[id], _water);
      /* Capped: see WADE.cap. The raw number believes a floating ribbon. */
      const depth = wa.depth > WADE.cap ? WADE.cap : wa.depth;
      if (depth > WADE.shallow) {
        const t = clamp01((depth - WADE.shallow) / (WADE.deep - WADE.shallow));
        st.wading = depth;
        want *= lerp(1, WADE.slow, t);
        /* The current is applied to POSITION, after the velocity solve, and
         * that placement is the whole of it working.
         *
         * The first version added it to e.vx/e.vz here, before the accel step
         * — where `want` is 0 for a standing player, so the very next lines
         * pulled the velocity straight back toward zero at P.decel. The decel
         * beat the push about ten to one and a hero standing in four metres of
         * water drifted 0.00 m in two seconds. A current is not something you
         * accelerate to; it moves you whether you are trying or not. */
        if (depth > WADE.outOfDepth) {
          st.current = Math.min(1, (depth - WADE.outOfDepth) / WADE.outOfDepth);
          st.currentX = wa.px; st.currentZ = wa.pz;
        }
      }
    }
    const tx = st.wantX * want, tz = st.wantZ * want;
    const a = (want > 0 ? P.accel : P.decel) * dt * st.mountAccel;
    e.vx[id] += clamp(tx - e.vx[id], -a, a);
    e.vz[id] += clamp(tz - e.vz[id], -a, a);
    const H = CFG.world.half - 8;
    e.x[id] = clamp(e.x[id] + e.vx[id] * dt, -H, H);
    e.z[id] = clamp(e.z[id] + e.vz[id] * dt, -H, H);
    /* ...and the current, after the integration it must not be damped by. */
    if (st.current > 0) {
      const c = WADE.push * st.current * dt;
      e.x[id] = clamp(e.x[id] + st.currentX * c, -H, H);
      e.z[id] = clamp(e.z[id] + st.currentZ * c, -H, H);
      st.current = 0;
    }

    const spd = Math.hypot(e.vx[id], e.vz[id]);
    st.speed = spd;
    if (st.locked && st.lockId >= 0) {
      const yg = Math.atan2(e.x[st.lockId] - e.x[id], e.z[st.lockId] - e.z[id]);
      e.yawGoal[id] = yg;
      e.yaw[id] = angleDamp(e.yaw[id], yg, P.turnRate * 0.8, dt);
    } else if (spd > 0.2) {
      e.yawGoal[id] = Math.atan2(e.vx[id], e.vz[id]);
      e.yaw[id] = angleDamp(e.yaw[id], e.yawGoal[id], P.turnRate, dt);
    }
    e.speed[id] = spd;
    /* A rider is not running. This line writes the hero's state EVERY frame
     * from his speed, so mount.js setting ST.MOUNTED once at mount time was
     * overwritten before a single frame drew and the seated clip never played
     * — the hero sprinted on the spot in the saddle. The state has to be
     * re-asserted here, where it is decided, not where riding begins. */
    if (st.mounted) e.state[id] = ST.MOUNTED;
    else if (!st.airborne && st.mode !== 'guard') e.state[id] = spd > 6.0 ? ST.RUN : spd > 0.4 ? ST.WALK : ST.IDLE;
  }

  /* --------------------------------------------------------- attack drive -- */

  function stepMove(w, dt) {
    const e = w.entities, id = st.id;
    const m = st.move;
    if (!m) { st.mode = 'free'; return; }

    const f0 = st.moveF;
    st.moveT += dt;
    const f = st.moveT / F;
    st.moveF = f;

    // phase -> entity state, which is what the animator and the AI read
    if (f < m.windup) { st.phase = 'windup'; e.state[id] = ST.WINDUP; }
    else if (f < m.windup + m.active) { st.phase = 'active'; e.state[id] = ST.ATTACK; }
    else { st.phase = 'recover'; e.state[id] = ST.RECOVER; }

    applyRoot(w, m, f);
    if (m.dive && st.airborne) { st.vy = Math.min(st.vy, -14); }

    // move-declared extra windows (shockwaves, musou rings)
    if (m.extra) {
      for (let i = 0; i < m.extra.length; i++) {
        const ov = m.extra[i];
        if (!(extraFired & (1 << i)) && f0 < ov.at && f >= ov.at) fireExtra(w, m, ov, i);
      }
    }

    // Musou anticipation owns only the authored slow-motion tail. Combat owns
    // connected finisher stop/shake/image/flash exactly once at sweep level.
    if ((m.presentation & PRESENTATION.MUSOU_FINISHER) && f0 < m.windup && f >= m.windup) {
      w.time.slowmo = 0.45; slowUntil = w.time.real + 0.42;
    }

    // steering: a little authority during the wind, none once the blade moves
    if (f < m.windup * 0.7 && !st.locked) {
      const l = Math.hypot(st.wantX, st.wantZ);
      if (l > 0.2) e.yaw[id] = angleDamp(e.yaw[id], Math.atan2(st.wantX, st.wantZ), 7, dt);
    }

    st.canCancel = f >= m.cancel.at;
    const bladeClosed = f >= m.windup + m.active;

    // cancels: musou any time after the wind, dodge once the blade is shut,
    // the next chain hit from the authored cancel frame.
    if (spendBuffer(w, st.canCancel, bladeClosed, f > m.windup * 0.5)) return;

    /* GUARD OUT OF RECOVERY — the missing cancel.
     *
     * spendBuffer() has branches for musou, dodge, jump and the next chain hit
     * and has never had one for GUARD, because guard is a held state rather
     * than a buffered press. The consequence was recorded as a Step 4 finding
     * and never chased: "YOU CANNOT GUARD OUT OF A SWING, and with the Great
     * Glaive a swing started before the wind-up does not finish before the
     * strike." freeAtStrike measured 0 of 5. Defence existed, was priced into
     * the economy, and could not be reached mid-combo by anyone.
     *
     * The window is `bladeClosed` — the same gate the dodge uses, i.e. once
     * the active frames are over. Not earlier: blocking out of your own active
     * frames would make the swing free. It opens slightly BEFORE the authored
     * chain cancel, which is deliberate — the player who reads a blow coming
     * should be able to answer it a few frames before the player who is only
     * mashing gets their next hit.
     *
     * It runs AFTER spendBuffer so a deliberate buffered input still wins: if
     * you asked for the next hit, you get the next hit. And `guardT = 0` means
     * the parry window opens fresh here, which is the whole point — the parry
     * becomes reachable mid-chain instead of only from a standstill.
     */
    if (bladeClosed && !st.airborne && st.guardBreakT <= 0 &&
        (w.input.held('guard') || w.input.held('interact'))) {
      endMove(w);
      st.mode = 'guard'; st.guarding = true; st.guardT = 0;
      e.state[id] = ST.BLOCK; e.stateT[id] = 0;
      return;
    }

    if (f >= m.total) { endMove(w); spendBuffer(w, true, true, true); }
  }
}
